import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HighlightsRail — sidebar widget that surfaces posts which were
 * pinned within the last 30 days but are no longer pinned. The point:
 * evergreen articles (the dental piece, the spay/neuter piece) shouldn't
 * vanish from the homepage just because a fresher pin took their slot.
 *
 * Self-hides when empty so brand-new communities don't see a sad
 * "0 highlights" placeholder. Lazy-fetches once on mount.
 */
export function HighlightsRail({ limit = 6 }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/posts/highlights`, { params: { limit } })
      .then((res) => setItems(Array.isArray(res.data) ? res.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, [limit]);

  if (!loaded || items.length === 0) return null;

  return (
    <div
      className="rounded-xl border border-border bg-card p-4"
      data-testid="highlights-rail"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-amber-500" />
        <h3 className="text-sm font-heading font-semibold">
          Past Highlights
        </h3>
      </div>
      <ul className="space-y-2.5">
        {items.map((item, idx) => (
          <li key={item.id} data-testid={`highlights-row-${idx}`}>
            <Link
              to={`/post/${item.id}`}
              className="block group"
            >
              <p className="text-sm leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                {item.title || '(untitled)'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {item.author_username || ''}
                {typeof item.comments_count === 'number' && item.comments_count > 0
                  ? ` · ${item.comments_count} comment${item.comments_count === 1 ? '' : 's'}`
                  : ''}
              </p>
            </Link>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-muted-foreground mt-3 italic">
        Recently pinned posts you may have missed.
      </p>
    </div>
  );
}

export default HighlightsRail;
